export function frameCopy(language: string | undefined) {
  const en = language?.toLowerCase().startsWith('en');
  if (en) {
    return {
      triage: 'Needs you',
      triageHint: 'Ordered by severity. The command goes to the local agent.',
      copyAgent: 'Copy for agent',
      copied: 'Copied for the agent',
      copyFailed: 'Clipboard unavailable. Select the command and copy it.',
      signoff: 'Waiting on you',
      none: '0',
      noneBody: 'Nothing here needs a sign-off.',
      signed: 'Signed. Waiting for npc to report back. This page did not change.',
      review: 'Check the diff and sign',
      again: 'Open the sign-off again',
      hold: 'Hold to sign and copy',
      holding: 'Keep holding…',
      holdHint: 'Hold for about a second. Release to cancel.',
      cancel: 'Cancel',
      limits: 'This page does not',
      agent: 'Hand to the agent',
      agentStart: 'A new session starts here. No raw SSH.',
      agentNote: 'Run this on the local machine. No raw SSH.',
      offlineWhy: 'Read the handoff before deciding whether the probe or the host failed.',
      probeWhy: 'Install the probe on this machine. It starts reporting after that.',
      briefWhy: 'Read the handoff.',
      playbookWhy: 'List the playbooks already on this host.',
      stale: 'The control plane is not responding.',
      staleBody: 'Below is a snapshot from',
      staleTail: 'Status may already have changed.',
      retry: 'Retry',
      palette: 'Jump or find a command',
      palettePh: 'Host, project, page, or subcommand',
      open: 'Open',
      copyOnly: 'Copy the command only',
      close: 'Close',
      noMatch: 'No match. Try a host, a project, or a subcommand such as deploy.',
      rebind: 'Certificate rebind',
      rebindRule: 'Signing only copies the npc command. This page does not change the certificate.',
      heartbeat: 'Probe heartbeats, last 30 minutes',
      lastBeat: 'Last heartbeat',
      neverBeat: 'No heartbeat yet',
      steady: 'A heartbeat every minute',
      now: 'now',
      ago: 'min ago',
    };
  }
  return {
    triage: '需要你看',
    triageHint: '按严重程度排。复制的命令交给本机 agent。',
    copyAgent: '复制给 agent',
    copied: '已复制，交给 agent',
    copyFailed: '剪贴板不可用。命令在页面上，请手动复制。',
    signoff: '等你签核',
    none: '0',
    noneBody: '没有要签核的变更。',
    signed: '已签核，等 npc 回报。这一页的数据没有变。',
    review: '核对差异并签核',
    again: '再看一遍签核单',
    hold: '按住签核并复制',
    holding: '继续按住…',
    holdHint: '按住约 1 秒签核，松手即取消',
    cancel: '取消',
    limits: '这页不做',
    agent: '交给 agent',
    agentStart: '新会话从这里开始，不走裸 SSH。',
    agentNote: '在本机执行，不走裸 SSH。',
    offlineWhy: '先读交接摘要，判断是探针还是主机出了问题',
    probeWhy: '在本机装探针，装好后这台机器才开始上报',
    briefWhy: '读交接摘要',
    playbookWhy: '看这台机器上有哪些现成 playbook',
    stale: '控制面没有响应。',
    staleBody: '下面是',
    staleTail: '的快照，状态可能已经变化。',
    retry: '重试',
    palette: '跳转或找命令',
    palettePh: '主机、项目、页面或子命令',
    open: '打开',
    copyOnly: '只复制命令',
    close: '关闭',
    noMatch: '没有匹配。试试主机名、项目名，或 deploy 这样的子命令。',
    rebind: '证书重绑',
    rebindRule: '签核只把 npc 命令放进剪贴板。这一页不改证书。',
    heartbeat: '近 30 分钟探针心跳',
    lastBeat: '最后一次心跳',
    neverBeat: '从未收到心跳',
    steady: '每分钟都有心跳',
    now: '现在',
    ago: '分钟前',
  };
}

export function pageLimits(pathname: string, language: string | undefined): string[] {
  const en = language?.toLowerCase().startsWith('en');
  const pick = (zh: string[], english: string[]) => (en ? english : zh);
  if (pathname === '/') {
    return pick(
      ['不在面板登记主机', '不执行命令、不开 SSH'],
      ['Does not enroll hosts', 'Does not run commands or open SSH'],
    );
  }
  if (pathname.startsWith('/servers/runtime')) {
    return pick(
      ['不在面板切换环境落点', '控制面自身发布用 npc system deploy'],
      ['Does not move an environment onto another host', 'Control-plane release uses npc system deploy'],
    );
  }
  if (pathname.startsWith('/servers/services')) {
    return pick(['不启停服务'], ['Does not start or stop services']);
  }
  if (pathname.startsWith('/servers/sites')) {
    return pick(['不写远端 Caddy / Nginx 配置', '不显示数据库凭证'], ['Does not write remote proxy config', 'Does not show database credentials']);
  }
  if (pathname.startsWith('/servers/domain-certificates')) {
    return pick(['不显示证书私钥', '签核只复制 npc cert rebind'], ['Does not show the private key', 'Sign-off only copies npc cert rebind']);
  }
  if (pathname.startsWith('/servers/timeline')) {
    return pick(['只读', '调用参数里的密钥不出现'], ['Read only', 'Secrets in call arguments are not shown']);
  }
  if (pathname.startsWith('/secrets')) {
    return pick(['不 reveal：只 inject', '密钥不进 argv、不进聊天'], ['No reveal, only inject', 'Secrets stay out of argv and chat']);
  }
  if (pathname.startsWith('/logs')) return pick(['只读'], ['Read only']);
  if (pathname.startsWith('/settings')) {
    return pick(['不回显 GitHub 令牌', '完整 API 密钥只显示一次'], ['Does not echo the GitHub token', 'A full API key is shown only once']);
  }
  if (pathname === '/projects') {
    return pick(
      ['控制面自身发布用 npc system deploy，不在这张表里'],
      ['Control-plane release uses npc system deploy. It is not in this table.'],
    );
  }
  if (pathname.startsWith('/projects')) {
    return pick(['不显示密钥值、令牌和 SSH 私钥'], ['Does not show secret values, tokens, or SSH keys']);
  }
  if (pathname === '/servers') {
    return pick(
      ['不登记主机：登记在本机 npc 完成', '不执行命令、不开 SSH'],
      ['Enrollment happens in local npc', 'Does not run commands or open SSH'],
    );
  }
  if (pathname.startsWith('/servers')) {
    return pick(['不执行、不上传、不改配置：都走 npc', '不显示 SSH 私钥'], ['Writes go through npc', 'Does not show the SSH private key']);
  }
  return pick(['写入仍由本机 npc 承担'], ['Writes stay on the local npc']);
}
